import React, { useMemo } from 'react';
import {
  BackgroundControlFileWithFile,
  BackgroundControlOrder,
  BackgroundControlWithFiles,
  BackgroundControlWithFilesRiskEnum,
  FindAllCompaniesResponse,
} from '@ansvar-sakerhet/api-client';
import { findLatestBackgroundControl } from '../utils/findLatestBackgroundControl';
import { findLatestBackgroundControlOrder } from '../utils/findLatestBackgroundControlOrder';
import { DownloadReportButton } from '../../DownloadReportButton';
import { RenameReportFileButton } from '../../../../../../../../../../controls/screens/CompaniesData/components/RenameReportFileButton';
import { findLatestReport } from '../utils/findLatestReport';
import { countCompanyFiles } from '../utils/companyBackgroundControls';
import { S } from '../SubcontractorsTable.style';
import { CompaniesSortControls } from '../../../../../../../../../../../traits/api/companies/useGetAllCompanies';

type SetSort = (sort: CompaniesSortControls) => void;

type Options = {
  /** When set, the table gets a column for deleting a company. */
  onDeleteCompany?: (company: FindAllCompaniesResponse) => void;
};

export const useCompaniesTableData = (
  companies: FindAllCompaniesResponse[],
  { onDeleteCompany }: Options = {}
) => {
  const columns = useMemo(() => {
    return [
      {
        Header: 'Namn',
        accessor: 'name',
        onClick: (setSort: SetSort, currentSort?: CompaniesSortControls) => {
          if (currentSort === CompaniesSortControls.NameAsc) {
            return setSort(CompaniesSortControls.NameDesc);
          }
          return setSort(CompaniesSortControls.NameAsc);
        },
        iconSrc: (currentSort?: CompaniesSortControls) => {
          if (currentSort === CompaniesSortControls.NameAsc)
            return 'ArrowDropUp';
          if (currentSort === CompaniesSortControls.NameDesc)
            return 'ArrowDropDown';
          return undefined;
        },
      },
      {
        Header: 'Org.nr',
        accessor: 'registrationNumber',
        onClick: (setSort: SetSort, currentSort?: CompaniesSortControls) => {
          if (currentSort === CompaniesSortControls.RegistrationNumberAsc) {
            return setSort(CompaniesSortControls.RegistrationNumberDesc);
          }
          return setSort(CompaniesSortControls.RegistrationNumberAsc);
        },
        iconSrc: (currentSort?: CompaniesSortControls) => {
          if (currentSort === CompaniesSortControls.RegistrationNumberAsc)
            return 'ArrowDropUp';
          if (currentSort === CompaniesSortControls.RegistrationNumberDesc)
            return 'ArrowDropDown';
          return undefined;
        },
      },
      {
        Header: 'Kontroll beställd',
        accessor: 'controlOrderedAt',
        onClick: undefined,
        iconSrc: undefined,
      },
      {
        Header: 'Senaste bakgrundskontroll',
        accessor: 'latestBackgroundControl',
        onClick: undefined,
        iconSrc: undefined,
      },
      {
        Header: 'Utgångsdatum',
        accessor: 'lastDate',
        onClick: (setSort: SetSort, currentSort?: CompaniesSortControls) => {
          if (currentSort === CompaniesSortControls.ExpirationDateAsc) {
            return setSort(CompaniesSortControls.ExpirationDateDesc);
          }
          return setSort(CompaniesSortControls.ExpirationDateAsc);
        },
        iconSrc: (currentSort?: CompaniesSortControls) => {
          if (currentSort === CompaniesSortControls.ExpirationDateAsc)
            return 'ArrowDropUp';
          if (currentSort === CompaniesSortControls.ExpirationDateDesc)
            return 'ArrowDropDown';
          return undefined;
        },
      },
      {
        Header: 'Rapport',
        accessor: 'latestReport',
        onClick: undefined,
        iconSrc: undefined,
      },
      {
        Header: 'Filnamn',
        accessor: 'latestReportFileName',
        onClick: undefined,
        iconSrc: undefined,
      },
      ...(onDeleteCompany
        ? [
            {
              Header: 'Radera',
              accessor: 'deleteCompany' as const,
              onClick: undefined,
              iconSrc: undefined,
            },
          ]
        : []),
    ] as const;
  }, [onDeleteCompany]);

  const data = useMemo(() => {
    return companies.map((company) => {
      const latestControlOrder = findLatestBackgroundControlOrder(company);
      const latestControl = findLatestBackgroundControl(company);
      const report = latestControl
        ? findLatestReport(latestControl)
        : undefined;

      return {
        name: `${company.name} (${countCompanyFiles(company)})`,
        registrationNumber: company.registrationNumber,
        controlOrderedAt: controlOrderedAt(latestControlOrder),
        latestBackgroundControl: latestBackgroundControl(latestControl),
        latestReport: latestReport(report),
        latestReportFileName: latestReportFileName(report),
        lastDate:
          latestControl && latestControl.lastDate
            ? latestControl.lastDate.split('T')[0]
            : '-',
        createBackgroundControl: <div></div>,
        deleteCompany: onDeleteCompany ? (
          <S.IconButton
            type="button"
            aria-label={`Radera ${company.name}`}
            onClick={() => onDeleteCompany(company)}
          >
            <S.Icon src="Trash" height={22} color="secondary" />
          </S.IconButton>
        ) : null,
        companyId: company.companyId,
      };
    });
  }, [companies, onDeleteCompany]);

  return { columns, data };
};

function controlOrderedAt(controlOrder: BackgroundControlOrder | undefined) {
  if (!controlOrder) return '-';

  const potentialDate = controlOrder.createdAt;
  const dateStr = potentialDate
    ? new Date(potentialDate).toLocaleDateString()
    : '-';

  return dateStr;
}

const AcceptanceTranslation: Record<
  BackgroundControlWithFilesRiskEnum,
  string
> = {
  NoRisk: 'Inga risker identifierade',
  LesserRisk: 'Avvikelser identifierade, OK att samarbeta',
  MediumHighRisk: 'Avvikelser identifierade, åtgärder föreslås',
  HighRisk:
    'Väsentliga risker Avrådan från att anlita företaget eller åtgärda avvikelser',
};

function latestBackgroundControl(
  control: BackgroundControlWithFiles | undefined
) {
  if (!control) return '-';

  const result = AcceptanceTranslation[control.risk];
  const controlDate = new Date(control.createdAt);
  const resText = `${result} - ${controlDate.toLocaleDateString()}`;

  return resText;
}

function latestReport(report: BackgroundControlFileWithFile | undefined) {
  if (!report) return '-';

  return <DownloadReportButton downloadUrl={report.file.url} />;
}

function latestReportFileName(
  report: BackgroundControlFileWithFile | undefined
) {
  if (!report) return '-';

  return (
    <RenameReportFileButton
      backgroundControlFileId={report.backgroundControlFileId}
      fileName={report.fileName}
    />
  );
}
